"use client";

import React from "react";
import { useTranslations } from "next-intl";
import posthog from "posthog-js";
import { Link } from "@/i18n/routing";
import { productRoutes } from "@/lib/navigation";
import { COOKIEBOT_ENABLED } from "@/lib/cookiebot";
import { COMPANY } from "@/lib/contact";

/**
 * Mirrors the contact-page click tracking (see ContactContent's
 * trackContactClick) so the footer's NAP block feeds the same outbound
 * answer-rate metric, tagged with its own location.
 */
function trackFooterContactClick(channel: "phone" | "email") {
  posthog.capture("contact_click", { channel, location: "footer" });
}

type NavLink = { href: string; label: string };

function FooterLinkGroup({
  title,
  links
}: {
  title: string;
  links: readonly NavLink[];
}) {
  return (
    <div>
      <h3 className="font-body text-xs font-extrabold uppercase !tracking-widest text-text-primary mb-4">
        {title}
      </h3>
      <ul className="space-y-3">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              className="text-sm text-text-muted hover:text-text-primary transition-colors duration-200"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Site-wide NAP block (name, address, phone). Search engines weigh a consistent
 * address that appears on every page; the values come from @/lib/contact so the
 * footer, the contact page and the Organization JSON-LD can never disagree.
 */
function FooterContactColumn({
  title,
  country,
  hours
}: {
  title: string;
  country: string;
  hours: string;
}) {
  return (
    <div>
      <h3 className="font-body text-xs font-extrabold uppercase !tracking-widest text-text-primary mb-4">
        {title}
      </h3>
      <address className="not-italic space-y-3 text-sm text-text-muted">
        <p className="leading-relaxed">
          {COMPANY.legalName}
          <br />
          {COMPANY.streetAddress}
          <br />
          {COMPANY.postalCode} {COMPANY.addressLocality}
          <br />
          {country}
        </p>
        <p className="leading-relaxed">
          <a
            href={`tel:${COMPANY.generalPhoneE164}`}
            onClick={() => trackFooterContactClick("phone")}
            className="hover:text-text-primary transition-colors duration-200"
          >
            {COMPANY.generalPhone}
          </a>
          <br />
          <a
            href={`mailto:${COMPANY.generalEmail}`}
            onClick={() => trackFooterContactClick("email")}
            className="hover:text-text-primary transition-colors duration-200 break-all"
          >
            {COMPANY.generalEmail}
          </a>
        </p>
        <p>{hours}</p>
      </address>
    </div>
  );
}

function FooterSocialLink({
  href,
  label,
  children
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-text-muted hover:text-text-primary transition-colors duration-200"
      aria-label={label}
    >
      {children}
    </a>
  );
}

export function Footer() {
  const t = useTranslations("Footer");

  const productLinks = productRoutes.map((r) => ({
    href: r.href,
    label: t(r.tKey)
  }));

  const companyLinks = [
    { href: "/about", label: t("about") },
    // TODO @saudin: Customers page coming — hidden for now, don't remove
    // { href: '/customers', label: t('customers') },
    { href: "/pricing", label: t("pricing") },
    { href: "/careers", label: t("careers") },
    { href: "/contact", label: t("contact") }
  ] as const;

  const resourceLinks = [
    { href: "/resources/scaffcalc-method", label: t("method") },
    {
      href: "/resources/news/best-scaffolding-software",
      label: t("bestSoftware")
    }
    // TODO @saudin: News & Blog page coming — hidden for now, don't remove
    // { href: '/resources/news', label: t('news') },
  ] as const;

  const legalLinks = [
    { href: "/legal/privacy-policy", label: t("privacy") },
    { href: "/legal/terms", label: t("terms") }
  ] as const;

  const handleRenewConsent = () => {
    window.Cookiebot?.renew?.();
  };

  return (
    <footer className="border-t border-border bg-bg-base">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid grid-cols-2 gap-8 lg:grid-cols-6">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <span className="font-body text-2xl font-bold text-text-primary ">
              Scaffcalc
            </span>
            <p className="mt-4 text-sm text-text-muted leading-relaxed">
              {t("tagline")}
            </p>
          </div>

          <FooterLinkGroup title={t("productTitle")} links={productLinks} />
          <FooterLinkGroup title={t("companyTitle")} links={companyLinks} />
          {/* Resources — hidden until blog / method pages are ready */}
          {resourceLinks.length > 0 && (
            <FooterLinkGroup
              title={t("resourcesTitle")}
              links={resourceLinks}
            />
          )}
          <FooterLinkGroup title={t("legalTitle")} links={legalLinks} />
          <FooterContactColumn
            title={t("contactTitle")}
            country={t("country")}
            hours={t("hours")}
          />
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 border-t border-border pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-x-4 gap-y-1">
            <p className="text-xs text-text-muted">{t("copyright")}</p>
            {COOKIEBOT_ENABLED && (
              <button
                type="button"
                onClick={handleRenewConsent}
                className="text-xs text-text-muted hover:text-text-primary transition-colors duration-200"
              >
                {t("cookieSettings")}
              </button>
            )}
          </div>
          <div className="flex gap-4">
            <FooterSocialLink
              href="https://www.youtube.com/@scaffcalc"
              label="YouTube"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" />
                <polygon
                  fill="var(--color-bg-base)"
                  points="9.545,15.568 15.818,12 9.545,8.432"
                />
              </svg>
            </FooterSocialLink>
            <FooterSocialLink
              href="https://www.linkedin.com/company/scaffcalc-ab/"
              label="LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
            </FooterSocialLink>
            <FooterSocialLink
              href="https://www.instagram.com/scaffcalc.se/"
              label="Instagram"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </FooterSocialLink>
            <FooterSocialLink
              href="https://www.facebook.com/scaffcalc.se"
              label="Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </FooterSocialLink>
          </div>
        </div>
      </div>
    </footer>
  );
}
